import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavHeader from "@/components/NavHeader";
import DiceRoller from "@/components/DiceRoller";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { loadLocalCharacters, upsertLocalCharacter, deleteLocalCharacter, loadLocalFoes, saveLocalFoe, deleteLocalFoe } from "@/lib/storage";
import { emptyCharacter } from "@/lib/character";
import { Trash2, Edit3, Plus, Cloud, HardDrive, Skull, Swords, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import CombatArena from "@/components/CombatArena";
import FoeBuilder from "@/components/FoeBuilder";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [characters, setCharacters] = useState([]);
  const [foes, setFoes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [arenaOpen, setArenaOpen] = useState(false);
  const [foeBuilderOpen, setFoeBuilderOpen] = useState(false);
  const [editingFoe, setEditingFoe] = useState(null);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);

  const load = async () => {
    setBusy(true);
    if (user) {
      try {
        const r = await api.get("/characters");
        setCharacters(r.data);
      } catch {
        toast.error("CLOUD FETCH FAILED — falling back to local");
        setCharacters(loadLocalCharacters());
      }
      try {
        const rf = await api.get("/foes");
        setFoes(rf.data || []);
      } catch {
        setFoes(loadLocalFoes());
      }
    } else {
      setCharacters(loadLocalCharacters());
      setFoes(loadLocalFoes());
    }
    setBusy(false);
  };

  useEffect(() => { if (!loading) load(); }, [loading, user]);

  const createNew = async () => {
    const c = emptyCharacter();
    if (user) {
      try {
        const { id, ...payload } = c;
        const r = await api.post("/characters", payload);
        navigate(`/character/${r.data.id}`);
        return;
      } catch {
        toast.error("Cloud create failed — creating locally");
      }
    }
    upsertLocalCharacter(c);
    navigate(`/character/${c.id}`);
  };

  const remove = async (id) => {
    if (!window.confirm("DELETE THIS PUNK? THIS IS PERMANENT.")) return;
    if (user) {
      try { await api.delete(`/characters/${id}`); } catch { toast.error("Delete failed"); return; }
    } else {
      deleteLocalCharacter(id);
    }
    toast("PUNK DELETED", { description: "One less soul in the wasteland." });
    load();
  };

  const removeFoe = async (id) => {
    if (!window.confirm("DELETE THIS FOE? THIS IS PERMANENT.")) return;
    if (user) {
      try { await api.delete(`/foes/${id}`); } catch { toast.error("Foe delete failed"); return; }
    } else {
      deleteLocalFoe(id);
    }
    toast("FOE DELETED");
    load();
  };

  const openFoeEditor = (foe = null) => {
    setEditingFoe(foe);
    setFoeBuilderOpen(true);
  };

  return (
    <div className="min-h-screen scanlines">
      <NavHeader />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              {user ? (
                <span className="tape tape-cyan flex items-center gap-1"><Cloud size={12} /> CLOUD SYNCED</span>
              ) : (
                <span className="tape flex items-center gap-1"><HardDrive size={12} /> LOCAL DEVICE</span>
              )}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter">
              YOUR <span className="text-[color:var(--accent-magenta)]">PUNKS</span>
            </h1>
            <p className="font-mono text-sm text-[color:var(--text-secondary)] mt-1">
              {user ? `Signed in as ${user.email}` : "Playing offline. Sign in to sync across devices."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="dashboard-arena-btn" onClick={() => setArenaOpen(true)} className="cy-btn cy-btn-magenta">
              <Swords size={16} className="mr-1" /> ARENA
            </button>
            <div className="relative">
              <button
                data-testid="create-menu-btn"
                onClick={() => setCreateMenuOpen((v) => !v)}
                className="cy-btn cy-btn-solid-yellow"
              >
                <Plus size={18} className="mr-1" /> NEW <ChevronDown size={14} className="ml-1" />
              </button>
              {createMenuOpen && (
                <div className="absolute right-0 top-full mt-1 z-30 panel p-1 min-w-[180px]" data-testid="create-menu-panel">
                  <button
                    data-testid="create-character-btn"
                    onClick={() => { setCreateMenuOpen(false); createNew(); }}
                    className="cy-btn cy-btn-cyan text-xs w-full justify-start mb-1"
                  >
                    NEW PUNK
                  </button>
                  <button
                    data-testid="create-foe-btn"
                    onClick={() => { setCreateMenuOpen(false); openFoeEditor(null); }}
                    className="cy-btn cy-btn-magenta text-xs w-full justify-start"
                  >
                    <Skull size={14} className="mr-1" /> FOE
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {busy && <div className="font-mono text-sm text-[color:var(--text-muted)]">LOADING…</div>}

        {!busy && characters.length === 0 && (
          <div className="panel p-8 text-center">
            <Skull className="mx-auto text-[color:var(--accent-magenta)]" size={48} />
            <div className="font-display text-2xl uppercase font-black mt-3">NO PUNKS YET.</div>
            <div className="font-mono text-sm text-[color:var(--text-secondary)] mt-2">Roll a new character and dive into the doomed future.</div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mt-6">
              <button onClick={createNew} data-testid="empty-create-btn" className="cy-btn cy-btn-solid-yellow">
                <Plus size={18} className="mr-1" /> CREATE FIRST PUNK
              </button>
              <button onClick={() => openFoeEditor(null)} data-testid="empty-create-foe-btn" className="cy-btn cy-btn-magenta">
                <Skull size={16} className="mr-1" /> CREATE FIRST FOE
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="character-grid">
          {characters.map((c) => (
            <div key={c.id} className="panel p-5 hover:panel-yellow transition-all group" data-testid={`character-card-${c.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--accent-cyan)] truncate">{c.char_class || "// UNCLASSIFIED"}</div>
                  <div className="font-display text-2xl font-black uppercase tracking-tight truncate">{c.name || "UNNAMED"}</div>
                </div>
                <div className="tape tape-magenta text-[10px]">HP {c.hp_current}/{c.hp_max}</div>
              </div>
              <div className="grid grid-cols-5 gap-1 mb-4 text-center">
                {["presence","strength","agility","toughness","knowledge"].map((k) => (
                  <div key={k} className="border-2 border-[color:var(--border-hard)] p-1">
                    <div className="text-[8px] font-mono uppercase text-[color:var(--text-muted)]">{k.slice(0,3)}</div>
                    <div className="font-display text-lg text-[color:var(--accent-yellow)]">{(c[k]||0)>=0?"+":""}{c[k]||0}</div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`edit-btn-${c.id}`} onClick={() => navigate(`/character/${c.id}`)} className="cy-btn text-xs flex-1">
                  <Edit3 size={12} className="mr-1" /> OPEN
                </button>
                <button data-testid={`delete-btn-${c.id}`} onClick={() => remove(c.id)} className="cy-btn cy-btn-magenta text-xs px-3" aria-label="Delete">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* FOE ROSTER */}
        {foes.length > 0 && (
          <div className="mt-10">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tighter">
                YOUR <span className="text-[color:var(--accent-magenta)]">FOES</span>
              </h2>
              <span className="tape tape-magenta text-[10px]">{foes.length} SAVED</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="foe-grid">
              {foes.map((f) => (
                <div key={f.id} className="panel panel-magenta p-5 hover:panel-yellow transition-all" data-testid={`foe-card-${f.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--accent-magenta)] truncate">FOE · MORALE {f.morale || 8}</div>
                      <div className="font-display text-2xl font-black uppercase tracking-tight truncate">{f.name || "UNNAMED"}</div>
                    </div>
                    <div className="tape text-[10px]">HP {f.hp}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-1 mb-3 text-center text-[10px] font-mono">
                    <div className="border-2 border-[color:var(--border-hard)] p-1">
                      <div className="text-[8px] uppercase text-[color:var(--text-muted)]">ARMOR</div>
                      <div className="font-display text-base text-[color:var(--accent-cyan)]">{f.armor > 0 ? `−d${f.armor}` : "0"}</div>
                    </div>
                    <div className="border-2 border-[color:var(--border-hard)] p-1">
                      <div className="text-[8px] uppercase text-[color:var(--text-muted)]">WEAPONS</div>
                      <div className="font-display text-base text-[color:var(--accent-yellow)]">{(f.weapons || []).length}</div>
                    </div>
                  </div>
                  {f.desc && <div className="font-mono text-[10px] text-[color:var(--text-secondary)] italic mb-3 line-clamp-2">{f.desc}</div>}
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`edit-foe-btn-${f.id}`}
                      onClick={() => openFoeEditor(f)}
                      className="cy-btn text-xs flex-1"
                    >
                      <Edit3 size={12} className="mr-1" /> EDIT
                    </button>
                    <button
                      data-testid={`delete-foe-btn-${f.id}`}
                      onClick={() => removeFoe(f.id)}
                      className="cy-btn cy-btn-magenta text-xs px-3"
                      aria-label="Delete foe"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      <DiceRoller character={null} />
      <CombatArena open={arenaOpen} onClose={() => { setArenaOpen(false); load(); }} roster={characters} />
      <FoeBuilder
        open={foeBuilderOpen}
        onClose={() => { setFoeBuilderOpen(false); setEditingFoe(null); }}
        foe={editingFoe}
        onSaved={() => { load(); toast.success(editingFoe ? "FOE UPDATED" : "FOE ADDED TO YOUR ROSTER"); }}
      />
    </div>
  );
}
